import authService from "services/authService";
import _ from "lodash";

//prefixes
const PERM = "perm:";
const ROLE = "role:";

//domains
const RECORDING_DOMAIN = PERM + "recording:";
const QUALITY_DOMAIN = PERM + "quality:";
const CONVERSATION_DOMAIN = PERM + "conversation:";
const COACHING_DOMAIN = PERM + "coaching:";
const AUDITS_DOMAIN = PERM + "audits:";
const ANALYTICS_DOMAIN = PERM + "analytics:";
const REALTIME_MONITOR_DOMAIN = PERM + "realtimeMonitor:";
const RELATE_DOMAIN = PERM + "relate:";
const EXTERNAL_CONTACTS_DOMAIN = PERM + "externalContacts:";
const ROUTING_DOMAIN = PERM + "routing:";

//entities
const DATA_RETENTION_ENTITY = ANALYTICS_DOMAIN + "dataRetention:";
const EVALUATION_ENTITY = QUALITY_DOMAIN + "evaluation:";
const CALIBRATION_ENTITY = QUALITY_DOMAIN + "calibration:";
const RECORDING_ENTITY = RECORDING_DOMAIN + "recording:";
const RECORDING_SEGMENT_ENTITY = RECORDING_DOMAIN + "recordingSegment:";
const ANNOTATION_ENTITY = RECORDING_DOMAIN + "annotation:";
const ENCRYPTION_KEY_ENTITY = RECORDING_DOMAIN + "encryptionKey:";
const RETENTION_POLICY_ENTITY = RECORDING_DOMAIN + "retentionPolicy:";
const EVALUATION_FORM_ENTITY = QUALITY_DOMAIN + "evaluationForm:";
const SURVEY_FORM_ENTITY = QUALITY_DOMAIN + "surveyForm:";
const ORPHAN_RECORDING_ENTITY = RECORDING_DOMAIN + "orphanRecording:";
const SETTINGS_ENTITY = RECORDING_DOMAIN + "settings:";
const SCREEN_RECORDING_ENTITY = RECORDING_DOMAIN + "screenRecording:";
const SNIPPET_RECORDING_ENTITY = RECORDING_DOMAIN + "snippetRecording:";
const SURVEY_ENTITY = QUALITY_DOMAIN + "survey:";
const CALL_ENTITY = CONVERSATION_DOMAIN + "call:";
const MESSAGE_ENTITY = CONVERSATION_DOMAIN + "message:";
const APPOINTMENT_ENTITY = COACHING_DOMAIN + "appointment:";
const DELETION_PROTECTION_ENTITY = RECORDING_DOMAIN + "deletionProtection:";
const AUDIT_INTERACTION_DETAILS_ENTITY = AUDITS_DOMAIN + "interactionDetails:";
const REALTIME_MONITOR_ENTITY = REALTIME_MONITOR_DOMAIN + "screen:";
const RELATE_CONTACT_ENTITY = RELATE_DOMAIN + "contact:";
const EXTERNAL_CONTACTS_CONTACT_ENTITY = EXTERNAL_CONTACTS_DOMAIN + "contact:";
const WRAPUP_CODE_ENTITY = ROUTING_DOMAIN + "wrapupCode:";

//temporary role names
const QUALITY_ADMIN_ROLE = ROLE + "qualityAdmin";
const QUALITY_EVALUATOR_ROLE = ROLE + "qualityEvaluator";
const AGENT = PERM + "routing:agent:onqueue";

//temporary role lists
const ADMIN_EVALUATOR = [QUALITY_ADMIN_ROLE, QUALITY_EVALUATOR_ROLE];
const ADMIN_EVALUATOR_SUPERVISOR = [QUALITY_ADMIN_ROLE, QUALITY_EVALUATOR_ROLE];
const ADMIN_EVALUATOR_AGENT = [
    QUALITY_ADMIN_ROLE,
    QUALITY_EVALUATOR_ROLE,
    AGENT,
];
const ADMIN_EVALUATOR_SUPERVISOR_AGENT = [
    QUALITY_ADMIN_ROLE,
    AGENT,
    QUALITY_EVALUATOR_ROLE,
];

//actions
const VIEW = "view";
const ADD = "add";
const DELETE = "delete";
const DISABLE = "disable";
const DOWNLOAD = "download";
const VIEW_AUDIT = "viewAudit";
const EDIT_RETENTION = "editRetention";
const EDIT = "edit";
const EDIT_SCORE = "editScore";
const EDIT_AGENT_SIGNOFF = "editAgentSignoff";
const RESTORE = "restore";
const STOP = "stop";
const EDIT_SCREEN_RECORDINGS = "editScreenRecordings";
const EDIT_REGIONAL_STORAGE = "editRegionalStorage";
const MONITOR = "monitor";
const MONITOR_AGENT = "monitorAgent";
const MONITOR_CONVERSATION = "monitorConversation";
const COACH = "coach";
const APPLY = "apply";
const REVOKE = "revoke";
const BARGE = "barge";

function checkPermission(permissions, roles, divisionIds) {
    const ignoreDivisions = !divisionIds ? true : false;
    let authorizations = [].concat(permissions);

    if (divisionIds && divisionIds.length > 0) {
        authorizations = authorizations.concat(
            _.flatMap(permissions, function (permission) {
                return _.map(divisionIds, function (divisionId) {
                    return permission + ":" + divisionId;
                });
            })
        );
    }
    authorizations = authorizations.concat(roles);
    return authService.hasAuthorization(
        { any: authorizations },
        ignoreDivisions
    );
}

export default {
    /**
     * Recordings
     */

    canApplyProtectionStatus(divisionIds) {
        return checkPermission(
            [DELETION_PROTECTION_ENTITY + APPLY],
            [],
            divisionIds
        );
    },

    canRevokeProtectionStatus(divisionIds) {
        return checkPermission(
            [DELETION_PROTECTION_ENTITY + REVOKE],
            [],
            divisionIds
        );
    },

    canViewRecording(divisionIds) {
        return checkPermission(
            [RECORDING_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    canViewRecordingSegment(divisionIds) {
        return checkPermission(
            [RECORDING_SEGMENT_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    canViewSnippetRecording(divisionIds) {
        return checkPermission(
            [SNIPPET_RECORDING_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    hasViewRecordingPermission(divisionIds) {
        return checkPermission(
            [RECORDING_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    hasViewRecordingSegmentPermission(divisionIds) {
        return checkPermission(
            [RECORDING_SEGMENT_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    canDownloadRecording(divisionIds) {
        return checkPermission([RECORDING_ENTITY + DOWNLOAD], [], divisionIds);
    },

    canViewRecordingAudit(divisionIds) {
        return checkPermission(
            [RECORDING_ENTITY + VIEW_AUDIT],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    canRestoreRecording(divisionIds) {
        return checkPermission(
            [RECORDING_ENTITY + RESTORE],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    canDeleteRecording(divisionIds) {
        return checkPermission(
            [RECORDING_ENTITY + DELETE],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    canEditRetention(divisionIds) {
        return checkPermission(
            [RECORDING_ENTITY + EDIT_RETENTION],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    canEditSnippetRecordingRetention(divisionIds) {
        return checkPermission(
            [SNIPPET_RECORDING_ENTITY + EDIT_RETENTION],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    canDownloadSnippetRecording(divisionIds) {
        return checkPermission(
            [SNIPPET_RECORDING_ENTITY + DOWNLOAD],
            [],
            divisionIds
        );
    },

    canRestoreSnippetRecording(divisionIds) {
        return checkPermission(
            [SNIPPET_RECORDING_ENTITY + RESTORE],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    /**
     * Annotations
     */

    canViewAnnotation(divisionIds) {
        return checkPermission(
            [ANNOTATION_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    canAddAnnotation(divisionIds) {
        return checkPermission(
            [ANNOTATION_ENTITY + ADD],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    canEditAnnotation(divisionIds) {
        return checkPermission(
            [ANNOTATION_ENTITY + EDIT],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    canDeleteAnnotation(divisionIds) {
        return checkPermission(
            [ANNOTATION_ENTITY + DELETE],
            ADMIN_EVALUATOR_SUPERVISOR_AGENT,
            divisionIds
        );
    },

    canViewAnnotationAudit(divisionIds) {
        return checkPermission(
            [ANNOTATION_ENTITY + VIEW_AUDIT],
            ADMIN_EVALUATOR_SUPERVISOR,
            divisionIds
        );
    },

    /**
     * Encryption Keys
     */

    canViewEncryptionKey() {
        return checkPermission(
            [ENCRYPTION_KEY_ENTITY + VIEW],
            QUALITY_ADMIN_ROLE
        );
    },

    canEditEncryptionKey() {
        return checkPermission(
            [ENCRYPTION_KEY_ENTITY + EDIT],
            QUALITY_ADMIN_ROLE
        );
    },

    /**
     * Evaluations
     */

    canAddEvaluation(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + ADD],
            [QUALITY_ADMIN_ROLE],
            divisionIds
        );
    },

    canDeleteEvaluation(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + DELETE],
            [QUALITY_ADMIN_ROLE],
            divisionIds
        );
    },

    canViewEvaluation(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + VIEW],
            ADMIN_EVALUATOR_AGENT,
            divisionIds
        );
    },

    canEditEvaluation(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + EDIT],
            [QUALITY_ADMIN_ROLE],
            divisionIds
        );
    },

    canEditScore(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + EDIT_SCORE],
            ADMIN_EVALUATOR,
            divisionIds
        );
    },

    canEditAgentSignoff(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + EDIT_AGENT_SIGNOFF],
            ADMIN_EVALUATOR_AGENT,
            divisionIds
        );
    },

    canViewEvaluationAudit(divisionIds) {
        return checkPermission(
            [EVALUATION_ENTITY + VIEW_AUDIT],
            ADMIN_EVALUATOR,
            divisionIds
        );
    },

    /**
     * Calibrations
     */
    canAddCalibration() {
        return checkPermission(
            [CALIBRATION_ENTITY + ADD],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canDeleteCalibration() {
        return checkPermission(
            [CALIBRATION_ENTITY + DELETE],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canViewCalibration() {
        return checkPermission(
            [CALIBRATION_ENTITY + VIEW],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canEditCalibration() {
        return checkPermission(
            [CALIBRATION_ENTITY + EDIT],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canViewCalibrationAudit() {
        return checkPermission(
            [CALIBRATION_ENTITY + VIEW_AUDIT],
            [QUALITY_ADMIN_ROLE]
        );
    },

    /**
     * Audits
     */

    canViewAudit() {
        return checkPermission(
            [AUDIT_INTERACTION_DETAILS_ENTITY + VIEW],
            ADMIN_EVALUATOR_SUPERVISOR
        );
    },

    /**
     * Retention Policies
     */

    canAddRetentionPolicy() {
        return checkPermission(
            [RETENTION_POLICY_ENTITY + ADD],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canDeleteRetentionPolicy() {
        return checkPermission(
            [RETENTION_POLICY_ENTITY + DELETE],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canViewRetentionPolicy() {
        return checkPermission(
            [RETENTION_POLICY_ENTITY + VIEW],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canEditRetentionPolicy() {
        return checkPermission(
            [RETENTION_POLICY_ENTITY + EDIT],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canViewOrphanRecording() {
        return checkPermission([ORPHAN_RECORDING_ENTITY + VIEW], []);
    },

    canDeleteOrphanRecording() {
        return checkPermission([ORPHAN_RECORDING_ENTITY + DELETE], []);
    },

    canEditOrphanRecording() {
        return checkPermission([ORPHAN_RECORDING_ENTITY + EDIT], []);
    },

    /**
     * Survey Forms
     */

    canAddSurveyForm() {
        return checkPermission([SURVEY_FORM_ENTITY + ADD], []);
    },

    canDeleteSurveyForm() {
        return checkPermission([SURVEY_FORM_ENTITY + DELETE], []);
    },

    canViewSurveyForm() {
        return checkPermission([SURVEY_FORM_ENTITY + VIEW], []);
    },

    canEditSurveyForm() {
        return checkPermission([SURVEY_FORM_ENTITY + EDIT], []);
    },

    canDisableSurveyForm() {
        return checkPermission([SURVEY_FORM_ENTITY + DISABLE], []);
    },

    /**
     * Surveys
     */

    canViewSurvey(divisionIds) {
        return checkPermission([SURVEY_ENTITY + VIEW], [], divisionIds);
    },

    /**
     * Evaluation Forms
     */

    canAddEvaluationForm() {
        return checkPermission(
            [EVALUATION_FORM_ENTITY + ADD],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canDeleteEvaluationForm() {
        return checkPermission(
            [EVALUATION_FORM_ENTITY + DELETE],
            [QUALITY_ADMIN_ROLE]
        );
    },

    canViewEvaluationForm() {
        return checkPermission(
            [EVALUATION_FORM_ENTITY + VIEW],
            ADMIN_EVALUATOR_AGENT
        );
    },

    canEditEvaluationForm() {
        return checkPermission(
            [EVALUATION_FORM_ENTITY + EDIT],
            [QUALITY_ADMIN_ROLE]
        );
    },

    /**
     * Screen Recordings
     */

    canViewScreenRecording(divisionIds) {
        return checkPermission(
            [SCREEN_RECORDING_ENTITY + VIEW],
            [],
            divisionIds
        );
    },

    canStopScreenRecording(divisionIds) {
        return checkPermission(
            [SCREEN_RECORDING_ENTITY + STOP],
            [],
            divisionIds
        );
    },

    canEditScreenRecordingRetention(divisionIds) {
        return checkPermission(
            [SCREEN_RECORDING_ENTITY + EDIT_RETENTION],
            [],
            divisionIds
        );
    },

    canViewScreenRecordingAudits(divisionIds) {
        return checkPermission(
            [SCREEN_RECORDING_ENTITY + VIEW_AUDIT],
            [],
            divisionIds
        );
    },

    canRestoreScreenRecordings(divisionIds) {
        return checkPermission(
            [SCREEN_RECORDING_ENTITY + RESTORE],
            [],
            divisionIds
        );
    },

    /**
     * Recording Settings for screen recordings
     */

    canEditScreenRecordingSettings() {
        return checkPermission([SETTINGS_ENTITY + EDIT_SCREEN_RECORDINGS], []);
    },

    /**
     * Edit WEM-440 regional recording storage settings
     */

    canEditRegionalRecordingStorageSettings(divisionIds) {
        return checkPermission(
            [SETTINGS_ENTITY + EDIT_REGIONAL_STORAGE],
            [],
            divisionIds
        );
    },

    /**
     * Call monitor
     */

    canMonitorCall(divisionIds) {
        return checkPermission([CALL_ENTITY + MONITOR], [], divisionIds);
    },

    /**
     * Call coach
     */

    canCoachCall(divisionIds) {
        return checkPermission([CALL_ENTITY + COACH], [], divisionIds);
    },

    /**
     * Schedule Coaching
     */
    canScheduleCoaching: function (divisionIds) {
        return checkPermission([APPOINTMENT_ENTITY + ADD], [], divisionIds);
    },

    /**
     * Call barge
     */

    canBargeInCall(divisionIds) {
        return checkPermission([CALL_ENTITY + BARGE], [], divisionIds);
    },

    /**
     * Message Monitoring
     */

    canMonitorMessaging(divisionIds) {
        return checkPermission([MESSAGE_ENTITY + MONITOR], [], divisionIds);
    },

    /**
     * Live screen monitoring - Agent level
     */
    canScreenMonitorAgentLevel(divisionIds) {
        return checkPermission(
            [REALTIME_MONITOR_ENTITY + MONITOR_AGENT],
            [],
            divisionIds
        );
    },

    /**
     * Live screen monitoring - Conversation level
     */
    canScreenMonitorConversationLevel(divisionIds) {
        return checkPermission(
            [REALTIME_MONITOR_ENTITY + MONITOR_CONVERSATION],
            [],
            divisionIds
        );
    },

    /**
     * Analytics
     */

    canViewAnalyticsDataretentionSettings() {
        return checkPermission([DATA_RETENTION_ENTITY + VIEW], []);
    },

    /**
     * Relate
     */
    canViewRelateContact() {
        return checkPermission([RELATE_CONTACT_ENTITY + VIEW], []);
    },

    /**
     * External Contacts
     */
    canViewExternalContactsContact(divisionIds) {
        return checkPermission(
            [EXTERNAL_CONTACTS_CONTACT_ENTITY + VIEW],
            [],
            divisionIds
        );
    },

    /**
     * Wrap-up Codes
     */
    canViewWrapupCode(divisionIds) {
        return checkPermission([WRAPUP_CODE_ENTITY + VIEW], [], divisionIds);
    },
};
